import { useCallback, useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Headphones, ChevronDown } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { ChatThread } from "@/components/support/ChatParts";
import { AssignControls } from "@/components/support/AssignControls";
import { ReopenChatButton } from "@/components/support/ReopenChatButton";

const BASE = "/support-assign";

function ChatPanel({ chatId, L, onChanged }) {
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get(`${BASE}/chats/${chatId}`).then(({ data }) => setD(data)).catch(() => {}), [chatId]);
  useEffect(() => { load(); const id = setInterval(load, 5000); return () => clearInterval(id); }, [load]);
  if (!d) return <p className="text-center text-slate-400 py-6">…</p>;
  return (
    <div className="border-t border-[#EFEBE0]" data-testid={`assign-chat-panel-${chatId}`}>
      <div className="p-3">{d.chat.status === "closed"
        ? <ReopenChatButton chatId={chatId} base={BASE} L={L} onDone={() => { load(); onChanged(); }} />
        : <AssignControls chat={d.chat} L={L} base={BASE} onDone={() => { load(); onChanged(); }} />}</div>
      <div className="h-[50vh] flex flex-col"><ChatThread messages={d.messages} customerName={d.chat.user_name} testid="assign-chat-thread" /></div>
    </div>
  );
}

export default function SupportAssign() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { lang, dir } = useLang();
  const L = (ar, en) => (lang === "en" ? en : ar);
  const Back = dir === "rtl" ? ChevronRight : ChevronLeft;
  const [list, setList] = useState(null);
  const [open, setOpen] = useState(null);
  const [status, setStatus] = useState("open");
  const pull = useCallback(() => api.get(`${BASE}/chats`, { params: { status } }).then(({ data }) => setList(data)).catch(() => setList([])), [status]);
  useEffect(() => { pull(); const id = setInterval(pull, 10000); return () => clearInterval(id); }, [pull]);
  if (user && !user.can_assign_support) return <Navigate to="/account" replace />;
  return (
    <div className="min-h-screen bg-[#FAF8F3]">
      <Navbar />
      <main className="max-w-3xl mx-auto px-4 py-6" data-testid="support-assign-page">
        <button onClick={() => navigate("/account")} className="text-slate-500 text-sm mb-4 flex items-center gap-1" data-testid="support-assign-back"><Back className="w-4 h-4" />{L("رجوع", "Back")}</button>
        <h1 className="font-headline text-2xl font-bold mb-1">{L("توزيع محادثات الدعم", "Distribute support chats")}</h1>
        <p className="text-sm text-slate-500 mb-4">{L("أسند كل محادثة مفتوحة لموظف أو لأي مستخدم، أو أعد فتح محادثة مغلقة", "Assign each open chat to an agent or any user, or reopen a closed chat")}</p>
        <div className="flex gap-2 mb-5">{[["open", "مفتوحة", "Open"], ["closed", "مغلقة", "Closed"]].map(([id, ar, en]) => (
          <button key={id} onClick={() => { setStatus(id); setOpen(null); setList(null); }} className={`h-9 px-4 rounded-full text-sm font-bold border ${status === id ? "bg-[#0F4D32] text-white border-[#0F4D32]" : "bg-white border-[#EFEBE0] text-slate-600"}`} data-testid={`support-assign-filter-${id}`}>{L(ar, en)}</button>))}</div>
        {!list ? <p className="text-slate-400">…</p> : !list.length ? <p className="text-center text-slate-500 py-10" data-testid="support-assign-empty">{status === "open" ? L("لا توجد محادثات مفتوحة", "No open chats") : L("لا توجد محادثات مغلقة", "No closed chats")}</p>
          : <div className="space-y-3" data-testid="support-assign-list">{list.map((c) => (
            <div key={c.chat_id} className="bg-white rounded-2xl border border-[#EFEBE0] overflow-hidden" data-testid={`support-assign-item-${c.chat_id}`}>
              <button onClick={() => setOpen((x) => (x === c.chat_id ? null : c.chat_id))} className="w-full text-start p-4 flex items-start gap-3" data-testid={`support-assign-toggle-${c.chat_id}`}>
                <span className="w-10 h-10 rounded-full bg-[#E8EFEA] flex items-center justify-center shrink-0"><Headphones className="w-5 h-5 text-[#1A5C3A]" /></span>
                <span className="flex-1 min-w-0">
                  <span className="block font-bold truncate">{c.user_name}</span>
                  <span className="block text-sm text-slate-600 truncate">{c.last_message}</span>
                  <span className="block text-xs text-slate-500 mt-1">{L("المسؤول", "Assignee")}: {c.agent_name || L("قيد الانتظار", "Unassigned")}
                    {c.assignee_type === "user" && <span className="ms-2 text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">{L("مستخدم مُسند", "Assigned user")}</span>}</span>
                </span>
                <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${open === c.chat_id ? "rotate-180" : ""}`} />
              </button>
              {open === c.chat_id && <ChatPanel chatId={c.chat_id} L={L} onChanged={pull} />}
            </div>))}</div>}
      </main>
    </div>
  );
}
